import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GET_CONSENSUS_PICK_STATS } from '@shared/graphql/queries/consensusPickStats';
import './MarketingTiles.css';
import '../../styles/MarketingTiles.css';
import '../../styles/TrackTypeBreakdown.css';

// Info icon component with tooltip
interface InfoIconProps {
  text: string;
}

const InfoIcon: React.FC<InfoIconProps> = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className="tile-info-icon"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={(e) => {
        e.stopPropagation();
        setShowTooltip(!showTooltip);
      }}
      role="button"
      tabIndex={0}
      aria-label={`Information about this statistic`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          setShowTooltip(!showTooltip);
        }
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
      {showTooltip && (
        <div className="tile-tooltip">
          {text}
        </div>
      )}
    </div>
  );
};

// GraphQL queries for marketing tiles
const TRACK_WITH_MOST_WINNERS = gql`
  query GetTrackWithMostWinners($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    trackWithMostWinners(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      trackName
      trackCode
      state
      winners
      races
      winPercentage
    }
  }
`;

const TRACK_WITH_BEST_ROI = gql`
  query GetTrackWithBestROI($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    trackWithBestROI(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      trackName
      trackCode
      state
      meetingDate
      winners
      races
      winPercentage
      roi
    }
  }
`;

const TRACK_WITH_BEST_VALUE_ROI = gql`
  query GetTrackWithBestValueROI($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    trackWithBestValueROI(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      trackName
      trackCode
      state
      meetingDate
      winners
      races
      winPercentage
      roi
    }
  }
`;

const AVERAGE_WINNER_SP = gql`
  query GetAverageWinnerSP($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    averageWinnerSP(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      averagePrice
      totalWinners
    }
  }
`;

const TOP_PICK_STATS_BY_TRACK_TYPE = gql`
  query GetTopPickStatsByTrackType($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    topPickStatsByTrackType(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      byTrackType {
        trackType
        winners
        races
        winPercentage
      }
      totalWinners
      totalRaces
      overallWinPercentage
    }
  }
`;

const TOTAL_WINNERS_STATS_BY_TRACK_TYPE = gql`
  query GetTotalWinnersStatsByTrackType($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    totalWinnersStatsByTrackType(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      byTrackType {
        trackType
        winners
        races
        winPercentage
      }
      totalWinners
      totalRaces
      overallSuccessRate
    }
  }
`;

const BIGGEST_WINNER = gql`
  query GetBiggestWinner($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    biggestWinner(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      horseName
      trackName
      trackCode
      state
      date
      meetingDate
      price
    }
  }
`;

const QUADDIE_STATS = gql`
  query GetQuaddieStats($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    quaddieStats(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      successfulQuaddies
      totalMeetingsAnalyzed
      successPercentage
    }
  }
`;

const EARLY_QUADDIE_STATS = gql`
  query GetEarlyQuaddieStats($analysisTypeId: Float = 1, $timePeriod: String = "7_DAYS") {
    earlyQuaddieStats(analysisTypeId: $analysisTypeId, timePeriod: $timePeriod) {
      successfulEarlyQuaddies
      totalMeetingsAnalyzed
      successPercentage
    }
  }
`;

interface MarketingTilesProps {
  heading?: string;
  analysisTypeId?: number;
}

const MarketingTiles: React.FC<MarketingTilesProps> = ({ heading, analysisTypeId = 1 }) => {
  // State for time period selection
  const [timePeriod, setTimePeriod] = useState<'7_DAYS' | '1_MONTH'>('1_MONTH');

  // Dynamic heading based on time period
  const getHeading = () => {
    if (heading && heading !== "Last 7 Days Performance") {
      return heading; // Custom heading provided
    }
    return timePeriod === '7_DAYS' ? 'Last 7 Days Performance' : 'Last Month Performance';
  };

  // Fetch data for all marketing tiles
  const { data: mostWinnersData, loading: mostWinnersLoading } = useQuery(TRACK_WITH_MOST_WINNERS, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: bestRoiData, loading: bestRoiLoading } = useQuery(TRACK_WITH_BEST_ROI, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: bestValueRoiData, loading: bestValueRoiLoading } = useQuery(TRACK_WITH_BEST_VALUE_ROI, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: avgWinnerSpData, loading: avgWinnerSpLoading } = useQuery(AVERAGE_WINNER_SP, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: topPickStatsByTrackTypeData, loading: topPickStatsByTrackTypeLoading } = useQuery(TOP_PICK_STATS_BY_TRACK_TYPE, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: totalWinnersStatsByTrackTypeData, loading: totalWinnersStatsByTrackTypeLoading } = useQuery(TOTAL_WINNERS_STATS_BY_TRACK_TYPE, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: biggestWinnerData, loading: biggestWinnerLoading } = useQuery(BIGGEST_WINNER, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: quaddieStatsData, loading: quaddieStatsLoading } = useQuery(QUADDIE_STATS, {
    variables: { analysisTypeId, timePeriod }
  });
  const { data: earlyQuaddieStatsData, loading: earlyQuaddieStatsLoading } = useQuery(EARLY_QUADDIE_STATS, {
    variables: { analysisTypeId, timePeriod }
  });

  const { data: consensusPickData, loading: consensusPickLoading } = useQuery(GET_CONSENSUS_PICK_STATS, {
    variables: { timePeriod }
  });

  // Format ROI as a percentage with + or - sign
  const formatROI = (roi: number | null | undefined) => {
    if (roi === null || roi === undefined || isNaN(roi)) return '0.0%';
    const formattedROI = (roi * 100).toFixed(1);
    return roi >= 0 ? `+${formattedROI}%` : `${formattedROI}%`;
  };

  // Get CSS class for positive/negative values
  const getValueClass = (value: number | null | undefined) => {
    if (value === null || value === undefined || isNaN(value)) return 'neutral-value';
    return value >= 0 ? 'positive-value' : 'negative-value';
  };

  // Format price as currency
  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined || isNaN(price)) return '$0.00';
    return `$${price.toFixed(2)}`;
  };

  // Get authentication status and navigation
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Convert meeting date to YYYYMMDD format for URL
  const formatDateForUrl = (meetingDate: string | number): string => {
    let date: Date;
    
    // Handle different input formats
    if (typeof meetingDate === 'number') {
      // If it's a timestamp
      date = new Date(meetingDate);
    } else if (typeof meetingDate === 'string') {
      // If it's a string, check the format
      if (meetingDate.includes('-')) {
        // YYYY-MM-DD format - just remove dashes
        return meetingDate.replace(/-/g, '');
      } else if (/^\d+$/.test(meetingDate)) {
        // String that looks like a timestamp
        date = new Date(parseInt(meetingDate));
      } else {
        // Try parsing as a date string
        date = new Date(meetingDate);
      }
    } else {
      // Fallback
      date = new Date(meetingDate);
    }
    
    // Format as YYYYMMDD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}${month}${day}`;
  };

  // Generate tips page URL: /{state}/{trackCode}/{YYYYMMDD}
  const generateTipsUrl = (state: string, trackCode: string, meetingDate: string): string => {
    const formattedDate = formatDateForUrl(meetingDate);
    return `/${state.toLowerCase()}/${trackCode.toLowerCase()}/${formattedDate}/results`;
  };

  // Handle tile click for navigation
  const handleTileClick = (state: string, trackCode: string, meetingDate: string) => {
    if (state && trackCode && meetingDate) {
      const url = generateTipsUrl(state, trackCode, meetingDate);
      navigate(url);
    }
  };

  // Get track type color class
  const getTrackTypeColorClass = (trackType: string) => {
    switch (trackType.toLowerCase()) {
      case 'metropolitan':
        return 'track-type-metropolitan';
      case 'provincial':
        return 'track-type-provincial';
      case 'country':
        return 'track-type-country';
      default:
        return 'track-type-default';
    }
  };

  // Render track type breakdown
  const renderTrackTypeBreakdown = (trackTypes: any[]) => {
    if (!trackTypes || trackTypes.length === 0) {
      return null;
    }

    // Define the desired order: Metropolitan, Provincial, Country
    const trackTypeOrder = ['Metropolitan', 'Provincial', 'Country'];
    
    // Sort track types according to the desired order
    const sortedTrackTypes = [...trackTypes].sort((a, b) => {
      const indexA = trackTypeOrder.indexOf(a.trackType);
      const indexB = trackTypeOrder.indexOf(b.trackType);
      
      // If both are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only one is in the order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // If neither is in the order array, maintain original order
      return 0;
    });

    return (
      <div className="track-type-breakdown">
        {sortedTrackTypes.map((trackType) => (
          <div key={trackType.trackType} className="track-type-row">
            <div className={`track-type-indicator ${getTrackTypeColorClass(trackType.trackType)}`}></div>
            <span className="track-type-name">{trackType.trackType}</span>
            <span className="track-type-percentage">{trackType.winPercentage ? trackType.winPercentage.toFixed(1) : '0.0'}%</span>
            <span className="track-type-stats">({trackType.winners}/{trackType.races})</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={isAuthenticated ? "marketing-tiles" : "marketing-tiles-container"}>
      {(isAuthenticated || getHeading() !== "Our Track Record Speaks for Itself") && (
        <h2 className={isAuthenticated ? "marketing-tiles-heading" : "section-title"}>{getHeading()}</h2>
      )}
      
      {/* Time Period Toggle */}
      <div className="time-period-toggle">
        <div className="toggle-buttons">
          <button
            className={`toggle-btn ${timePeriod === '7_DAYS' ? 'active' : ''}`}
            onClick={() => setTimePeriod('7_DAYS')}
          >
            7 Days
          </button>
          <button
            className={`toggle-btn ${timePeriod === '1_MONTH' ? 'active' : ''}`}
            onClick={() => setTimePeriod('1_MONTH')}
          >
            1 Month
          </button>
        </div>
      </div>
      
      <div className="marketing-tiles-grid">
        {/* Top Pick Stats Tile */}
        <div className="marketing-tile">
          <div className="tile-header orange">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <h3 className="tile-title">{isAuthenticated ? "Top Pick Performance" : "Top Pick Winners"}</h3>
            <InfoIcon text="Our #1 tip selections that won their race. Shows the percentage of races where our top-rated horse finished first, broken down by track type (Metropolitan, Provincial, Country)." />
          </div>
          
          {topPickStatsByTrackTypeLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : topPickStatsByTrackTypeData?.topPickStatsByTrackType ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="win-percentage">{topPickStatsByTrackTypeData.topPickStatsByTrackType.overallWinPercentage ? topPickStatsByTrackTypeData.topPickStatsByTrackType.overallWinPercentage.toFixed(1) : '0.0'}%</span>
              </div>
              <div className="tile-sub-stat">
                <span>{topPickStatsByTrackTypeData.topPickStatsByTrackType.totalWinners} winners from {topPickStatsByTrackTypeData.topPickStatsByTrackType.totalRaces} races</span>
              </div>
              {renderTrackTypeBreakdown(topPickStatsByTrackTypeData.topPickStatsByTrackType.byTrackType)}
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>
        
        {/* Prime Pick Performance Tile */}
        <div className="marketing-tile">
          <div className="tile-header" style={{ background: 'linear-gradient(135deg, #F1C40F 0%, #F39C12 100%)' }}>
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <h3 className="tile-title" style={{ color: '#1a1a1a' }}>Prime Pick Performance</h3>
            <InfoIcon text="Prime Picks are tips where both our models independently select the same horse as their #1 pick. These show a higher strike rate than either model alone." />
          </div>

          {consensusPickLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : (() => {
            const rows = consensusPickData?.consensusPickStats?.rows ?? [];
            const total = rows.find((r: any) => r.trackType === 'total');
            const byType = ['metropolitan', 'provincial', 'country']
              .map((tt: string) => rows.find((r: any) => r.trackType === tt))
              .filter(Boolean);
            if (!total || total.agreeRaces === 0) return <div className="tile-no-data">No data available</div>;
            const trackLabels: Record<string, string> = { metropolitan: 'Metropolitan', provincial: 'Provincial', country: 'Country' };
            return (
              <div className="tile-content">
                <div className="tile-main-stat">
                  <span className="win-percentage">{total.agreeStrikePct?.toFixed(1) ?? '0.0'}%</span>
                </div>
                <div className="tile-sub-stat">
                  <span>{total.agreeWins} winners from {total.agreeRaces} tips</span>
                </div>
                {renderTrackTypeBreakdown(byType.map((r: any) => ({
                  trackType: trackLabels[r.trackType] ?? r.trackType,
                  winners: r.agreeWins,
                  races: r.agreeRaces,
                  winPercentage: r.agreeStrikePct,
                })))}
              </div>
            );
          })()}
        </div>

        {/* Track with Most Winners Tile */}
        <div className="marketing-tile">
          <div className="tile-header purple">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="tile-title">{isAuthenticated ? "Top Performing Track" : "Most Winners Track"}</h3>
            <InfoIcon text="The racetrack with the highest number of winners from our top picks during this period. Shows which venue our analysis performs best at." />
          </div>
          
          {mostWinnersLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : mostWinnersData?.trackWithMostWinners ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="track-name">{mostWinnersData.trackWithMostWinners.trackName}</span>
                <span className="win-percentage">{mostWinnersData.trackWithMostWinners.winPercentage ? mostWinnersData.trackWithMostWinners.winPercentage.toFixed(1) : '0.0'}%</span>
              </div>
              <div className="tile-sub-stat">
                <span>{mostWinnersData.trackWithMostWinners.winners} winners from {mostWinnersData.trackWithMostWinners.races} races</span>
              </div>
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>
        
        {/* Track with Best ROI Tile */}
        <div 
          className={`marketing-tile ${bestRoiData?.trackWithBestROI?.meetingDate ? 'clickable' : ''}`}
          onClick={() => {
            if (bestRoiData?.trackWithBestROI?.meetingDate) {
              handleTileClick(
                bestRoiData.trackWithBestROI.state,
                bestRoiData.trackWithBestROI.trackCode,
                bestRoiData.trackWithBestROI.meetingDate
              );
            }
          }}
          style={{ cursor: bestRoiData?.trackWithBestROI?.meetingDate ? 'pointer' : 'default' }}
        >
          <div className="tile-header green">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="tile-title">Best ROI Track</h3>
            <InfoIcon text="Return on Investment (ROI) - The track with the best percentage return if you placed $1 on every race using our top picks. Click to view the meeting results." />
          </div>
          
          {bestRoiLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : bestRoiData?.trackWithBestROI ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="track-name">{bestRoiData.trackWithBestROI.trackName}</span>
                <span className={`roi-value ${getValueClass(bestRoiData.trackWithBestROI.roi)}`}>
                  {formatROI(bestRoiData.trackWithBestROI.roi)}
                </span>
              </div>
              <div className="tile-sub-stat">
                <span>{bestRoiData.trackWithBestROI.winners} winners from {bestRoiData.trackWithBestROI.races} races</span>
              </div>
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>
        
        {/* Track with Best Value ROI Tile */}
        <div 
          className={`marketing-tile ${bestValueRoiData?.trackWithBestValueROI?.meetingDate ? 'clickable' : ''}`}
          onClick={() => {
            if (bestValueRoiData?.trackWithBestValueROI?.meetingDate) {
              handleTileClick(
                bestValueRoiData.trackWithBestValueROI.state,
                bestValueRoiData.trackWithBestValueROI.trackCode,
                bestValueRoiData.trackWithBestValueROI.meetingDate
              );
            }
          }}
          style={{ cursor: bestValueRoiData?.trackWithBestValueROI?.meetingDate ? 'pointer' : 'default' }}
        >
          <div className="tile-header blue">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="tile-title">{isAuthenticated ? "Best Value Bet Track" : "Best Value Bet"}</h3>
            <InfoIcon text="The track with the best ROI for our Value Bet selections (4th pick). Value bets are typically longer-priced horses that offer good value. Click to view the meeting results." />
          </div>
          
          {bestValueRoiLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : bestValueRoiData?.trackWithBestValueROI ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="track-name">{bestValueRoiData.trackWithBestValueROI.trackName}</span>
                <span className={`roi-value ${getValueClass(bestValueRoiData.trackWithBestValueROI.roi)}`}>
                  {formatROI(bestValueRoiData.trackWithBestValueROI.roi)}
                </span>
              </div>
              <div className="tile-sub-stat">
                <span>{bestValueRoiData.trackWithBestValueROI.winners} winners from {bestValueRoiData.trackWithBestValueROI.races} races</span>
              </div>
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>
        
        {/* Average Winner SP Tile */}
        <div className="marketing-tile">
          <div className="tile-header red">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="tile-title">{isAuthenticated ? "Average Winner Price" : "Avg Winner Price"}</h3>
            <InfoIcon text="The average starting price (SP) of all our winning selections during this period. This shows the typical odds of our successful picks." />
          </div>
          
          {avgWinnerSpLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : avgWinnerSpData?.averageWinnerSP ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="average-price">{formatPrice(avgWinnerSpData.averageWinnerSP.averagePrice)}</span>
              </div>
              <div className="tile-sub-stat">
                <span>Based on {avgWinnerSpData.averageWinnerSP.totalWinners} winners</span>
              </div>
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>
        
        {/* Total Winners Stats Tile */}
        <div className="marketing-tile">
          <div className="tile-header cyan">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <h3 className="tile-title">{isAuthenticated ? "Total Winners (All Tips)" : "Overall Success Rate"}</h3>
            <InfoIcon text="Combined winners from all our tip types (Top Pick, 2nd Pick, 3rd Pick, and Value Bet). Shows our overall success rate across all four selections per race, broken down by track type." />
          </div>
          
          {totalWinnersStatsByTrackTypeLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : totalWinnersStatsByTrackTypeData?.totalWinnersStatsByTrackType ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="success-rate">{totalWinnersStatsByTrackTypeData.totalWinnersStatsByTrackType.overallSuccessRate ? totalWinnersStatsByTrackTypeData.totalWinnersStatsByTrackType.overallSuccessRate.toFixed(1) : '0.0'}%</span>
              </div>
              <div className="tile-sub-stat">
                <span>{totalWinnersStatsByTrackTypeData.totalWinnersStatsByTrackType.totalWinners} winners from {totalWinnersStatsByTrackTypeData.totalWinnersStatsByTrackType.totalRaces} races</span>
                <div className="tile-description">
                  <small>Combined success rate across all 4 tip types</small>
                </div>
              </div>
              {renderTrackTypeBreakdown(totalWinnersStatsByTrackTypeData.totalWinnersStatsByTrackType.byTrackType)}
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>

        {/* Biggest Winner Tile */}
        <div
          className={`marketing-tile ${biggestWinnerData?.biggestWinner?.meetingDate ? 'clickable' : ''}`}
          onClick={() => {
            if (biggestWinnerData?.biggestWinner?.meetingDate) {
              handleTileClick(
                biggestWinnerData.biggestWinner.state,
                biggestWinnerData.biggestWinner.trackCode,
                biggestWinnerData.biggestWinner.meetingDate
              );
            }
          }}
          style={{ cursor: biggestWinnerData?.biggestWinner?.meetingDate ? 'pointer' : 'default' }}
        >
          <div className="tile-header orange">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="tile-title">Biggest Winner</h3>
            <InfoIcon text="The highest-priced winner from our selections during this period. Shows our ability to identify longshot winners. Click to view the meeting results." />
          </div>

          {biggestWinnerLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : biggestWinnerData?.biggestWinner ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="horse-name">{biggestWinnerData.biggestWinner.horseName}</span>
                <span className="price">{formatPrice(biggestWinnerData.biggestWinner.price)}</span>
              </div>
              <div className="tile-sub-stat">
                <span>{biggestWinnerData.biggestWinner.trackName} - {biggestWinnerData.biggestWinner.date}</span>
              </div>
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>

        {/* Quaddie Success Tile */}
        <div className="marketing-tile">
          <div className="tile-header purple">
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="tile-title">Quaddie (Q4)</h3>
            <InfoIcon text="The number of meetings where our picks won all four quaddie legs (last 4 races). Shows how often our analysis nails the full quaddie." />
          </div>

          {quaddieStatsLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : quaddieStatsData?.quaddieStats ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="win-percentage">{quaddieStatsData.quaddieStats.successPercentage.toFixed(1)}%</span>
              </div>
              <div className="tile-sub-stat">
                <span>{quaddieStatsData.quaddieStats.successfulQuaddies} from {quaddieStatsData.quaddieStats.totalMeetingsAnalyzed} meetings</span>
              </div>
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>

        {/* Early Quaddie Tile */}
        <div className="marketing-tile">
          <div className="tile-header" style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
            <div className="tile-icon">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </div>
            <h3 className="tile-title">Early Quaddie (EQ)</h3>
            <InfoIcon text="The number of meetings where our picks won all four early quaddie legs. For 8+ race meetings these are the middle four races; for 7 race meetings they are races 1–4." />
          </div>

          {earlyQuaddieStatsLoading ? (
            <div className="tile-loading">Loading...</div>
          ) : earlyQuaddieStatsData?.earlyQuaddieStats ? (
            <div className="tile-content">
              <div className="tile-main-stat">
                <span className="win-percentage">{earlyQuaddieStatsData.earlyQuaddieStats.successPercentage.toFixed(1)}%</span>
              </div>
              <div className="tile-sub-stat">
                <span>{earlyQuaddieStatsData.earlyQuaddieStats.successfulEarlyQuaddies} from {earlyQuaddieStatsData.earlyQuaddieStats.totalMeetingsAnalyzed} meetings</span>
              </div>
            </div>
          ) : (
            <div className="tile-no-data">No data available</div>
          )}
        </div>
      </div>
      
      {/* ROI Explanation Footer */}
      <div className="mt-4 pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          * ROI (Return on Investment) represents the percentage return if you placed $1 on every race at the meeting using our top picks.
        </p>
      </div>
    </div>
  );
};

export default MarketingTiles;
